import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchWithAuth } from '../../utils/api';
import { useToast } from '../../contexts/ToastContext';
import { hashPassword } from '../../utils/helpers';
import RowsPerPageSelector from '../common/RowsPerPageSelector';
import Pagination from '../common/Pagination';
import ConfirmationModal from '../modals/ConfirmationModal';
import SortSelector from '../common/SortSelector';

function UserManagement({ user, handleLogout }) {
  const toast = useToast();
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ username: '', password: '', role: 'user' });
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null });
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'username', direction: 'asc' });
  const [systemSettings, setSystemSettings] = useState({});
  const [layoutMode, setLayoutMode] = useState(window.innerWidth < 768 ? 'cards' : 'table');

  const fetchUsers = useCallback(() => {
    fetchWithAuth('/users', {}, user, handleLogout)
      .then(res => res.json())
      .then(data => setUsers(data || []))
      .catch(err => console.error(err));
  }, [user, handleLogout]);

  const fetchSystemSettings = useCallback(() => {
    fetchWithAuth('/system/settings', {}, user, handleLogout)
      .then(res => res.json())
      .then(data => setSystemSettings(data || {}))
      .catch(err => console.error(err));
  }, [user, handleLogout]);

  useEffect(() => {
    fetchUsers();
    fetchSystemSettings();
  }, [fetchUsers, fetchSystemSettings]);

  // Track the narrow breakpoint with matchMedia so we transition in
  // both directions — the previous resize listener only shrank
  // table→cards on narrow and never restored on widen.
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 767px)');
    const apply = (matches) => setLayoutMode(matches ? 'cards' : 'table');
    apply(mql.matches);
    const listener = (e) => apply(e.matches);
    mql.addEventListener('change', listener);
    return () => mql.removeEventListener('change', listener);
  }, []);

  const isProtectedAdmin = (u) => {
    return u.username === 'admin' || u.is_google_user; 
  };

  const handleUpdateSetting = (key, value) => {
    fetchWithAuth('/system/settings/update', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key, value: value.toString() })
    }, user, handleLogout)
    .then(async res => {
      if (!res.ok) throw new Error(await res.text());
      fetchSystemSettings();
      toast.success('Setting updated.');
    })
    .catch(err => toast.error(err.message));
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [rowsPerPage, sortConfig]);

  const sortedUsers = useMemo(() => {
    let sortableItems = [...users];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }

        if (aVal < bVal) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [users, sortConfig]);

  const paginatedUsers = useMemo(() => {
    if (rowsPerPage === 'all') return sortedUsers;
    const start = (currentPage - 1) * rowsPerPage;
    return sortedUsers.slice(start, start + rowsPerPage);
  }, [sortedUsers, currentPage, rowsPerPage]);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');

    const hashedPassword = await hashPassword(newUser.password);

    fetchWithAuth('/users/create', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...newUser, password: hashedPassword })
    }, user, handleLogout)
    .then(async res => {
      if (!res.ok) throw new Error(await res.text());
      fetchUsers();
      setNewUser({ username: '', password: '', role: 'user' });
    })
    .catch(err => setError(err.message));
  };

  const handleDelete = (id) => {
    setDeleteConfirm({ isOpen: true, id });
  };

  const handleRoleChange = (id, nRole, currentUsername) => {
    if (currentUsername === 'admin') {
      toast.error("Cannot change role of the primary admin user.");
      return;
    }
    setError('');
    fetchWithAuth('/users/role', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, role: nRole })
    }, user, handleLogout)
    .then(async res => {
      if (!res.ok) throw new Error(await res.text());
      fetchUsers();
    })
    .catch(err => setError(err.message));
  };

  const handlePermissionToggle = (u, section) => {
    if (isProtectedAdmin(u)) return;
    
    let currentPerms = u.permissions ? u.permissions.split(',') : [];
    if (currentPerms.includes('all')) {
      currentPerms = ['sales', 'harvest_agents', 'mortality', 'employees']; 
    }

    let newPerms;
    if (currentPerms.includes(section)) {
      newPerms = currentPerms.filter(p => p !== section);
    } else {
      newPerms = [...currentPerms, section];
    }

    const permissionsString = newPerms.join(',');
    
    fetchWithAuth('/users/permissions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: u.id, permissions: permissionsString })
    }, user, handleLogout)
    .then(async res => {
      if (!res.ok) throw new Error(await res.text());
      fetchUsers();
    })
    .catch(err => setError(err.message));
  };

  const confirmDelete = () => {
    const id = deleteConfirm.id;
    fetchWithAuth(`/users/delete?id=${id}`, {
      method: 'POST'
    }, user, handleLogout)
    .then(async res => {
      if (!res.ok) throw new Error(await res.text());
      fetchUsers();
      setDeleteConfirm({ isOpen: false, id: null });
    })
    .catch(err => {
      setError(err.message);
      setDeleteConfirm({ isOpen: false, id: null });
    });
  };

  return (
    <div className="user-management-view">
      <div className="view-header">
        <h2>User Management</h2>
      </div>

      {error && <div className="error-message" style={{marginBottom: '1.5rem'}}>{error}</div>}
      
      <div className="list-controls">
        <div className="control-group">
          <RowsPerPageSelector value={rowsPerPage} onChange={setRowsPerPage} />
          {layoutMode === 'cards' && (
            <SortSelector 
              options={[
                { key: 'username', label: 'Username' },
                { key: 'role', label: 'Role' }
              ]} 
              sortConfig={sortConfig} 
              onSortChange={setSortConfig} 
            />
          )}
        </div>
        <div className="layout-toggle no-print">
          <button className={layoutMode === 'table' ? 'active' : ''} onClick={() => setLayoutMode('table')}>Table</button>
          <button className={layoutMode === 'cards' ? 'active' : ''} onClick={() => setLayoutMode('cards')}>Cards</button>
        </div>
      </div>

      {layoutMode === 'table' ? (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th onClick={() => requestSort('id')} className="sortable">ID {sortConfig.key === 'id' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                <th onClick={() => requestSort('username')} className="sortable">Username {sortConfig.key === 'username' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                <th onClick={() => requestSort('role')} className="sortable">Role {sortConfig.key === 'role' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                <th>Permissions</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedUsers.map(u => (
                <tr key={u.id}>
                  <td>{u.id}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {u.username}
                      {u.is_google_user && (
                        <span style={{ fontSize: '0.7rem', background: 'var(--google-blue)', color: 'white', padding: '0.1rem 0.4rem', borderRadius: '4px' }}>GOOGLE</span>
                      )}
                    </div>
                  </td>
                  <td>
                    {isProtectedAdmin(u) ? (
                      <span className="badge-admin" style={{background: 'var(--google-grey-100)', color: 'var(--google-grey-700)', padding: '0.2rem 0.6rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold'}}>{u.role.toUpperCase()}</span>
                    ) : (
                      <select 
                        value={u.role} 
                        onChange={(e) => handleRoleChange(u.id, e.target.value, u.username)}
                        className="table-select"
                        style={{padding: '0.2rem 0.5rem', borderRadius: '4px', border: '1px solid var(--google-grey-300)'}}
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                      </select>
                    )}
                  </td>
                  <td>
                    {isProtectedAdmin(u) ? (
                      <span className="full-access-text" style={{color: 'var(--google-green)', fontWeight: '500', fontSize: '0.85rem'}}>Full Access</span>
                    ) : (
                      <div className="permission-chips" style={{display: 'flex', flexWrap: 'wrap', gap: '0.5rem'}}>
                        {['sales', 'harvest_agents', 'mortality', 'employees'].map(sec => (
                          <label key={sec} className="permission-checkbox-label" style={{display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.8rem', background: 'var(--google-grey-50)', padding: '0.2rem 0.5rem', borderRadius: '4px', cursor: 'pointer'}}>
                            <input 
                              type="checkbox" 
                              checked={u.permissions?.split(',').includes(sec) || u.permissions === 'all'} 
                              onChange={() => handlePermissionToggle(u, sec)}
                            />
                            <span>{sec.replace('_', ' ')}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="row-actions">
                    {u.username !== user.username && !isProtectedAdmin(u) && (
                      <button className="cancel-btn sm" onClick={() => handleDelete(u.id)}>Delete</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card-grid">
          {paginatedUsers.map(u => (
            <div key={u.id} className="data-card user-card">
              <div className="card-header">
                <div className="card-title-group">
                  <h4>{u.username}</h4>
                  {u.is_google_user && (
                    <span style={{ fontSize: '0.65rem', background: 'var(--google-blue)', color: 'white', padding: '0.1rem 0.4rem', borderRadius: '4px' }}>GOOGLE</span>
                  )}
                </div>
                <span className="card-date">ID: {u.id}</span>
              </div>
              <div className="card-body">
                <div className="card-item">
                  <span className="label">Role</span>
                  <span className="value">
                    {isProtectedAdmin(u) ? (
                      <span style={{fontWeight: '600', color: 'var(--google-blue)'}}>{u.role.toUpperCase()}</span>
                    ) : (
                      <select 
                        value={u.role} 
                        onChange={(e) => handleRoleChange(u.id, e.target.value, u.username)}
                        style={{padding: '0.25rem', borderRadius: '4px'}}
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                      </select>
                    )}
                  </span>
                </div>
                <div className="card-item full-width">
                  <span className="label">Permissions</span>
                  <div className="value">
                    {isProtectedAdmin(u) ? (
                      <span style={{color: 'var(--google-green)', fontWeight: '500'}}>Full Access</span>
                    ) : (
                      <div className="permission-chips" style={{marginTop: '0.5rem'}}>
                        {['sales', 'harvest_agents', 'mortality', 'employees'].map(sec => (
                          <label key={sec} className="permission-checkbox-label">
                            <input 
                              type="checkbox" 
                              checked={u.permissions?.split(',').includes(sec) || u.permissions === 'all'} 
                              onChange={() => handlePermissionToggle(u, sec)}
                            />
                            <span>{sec.replace('_', ' ')}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="card-actions">
                {u.username !== user.username && !isProtectedAdmin(u) && (
                  <button className="cancel-btn sm" onClick={() => handleDelete(u.id)} style={{width: '100%'}}>Delete User</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Pagination 
        totalItems={users.length} 
        itemsPerPage={rowsPerPage} 
        currentPage={currentPage} 
        onPageChange={setCurrentPage} 
      />

      {/* System Settings — only for Google-authenticated users. Read from
          the server-issued user object (set by the GoogleLogin response)
          rather than localStorage, which was both flippable in DevTools and
          never actually set anywhere — so this gate was always false. The
          backend still enforces IsGoogleAdmin on the settings endpoint. */}
      {user?.is_google === 'true' && (
        <div className="form-section" style={{ marginTop: '2rem', border: '1px solid var(--google-red)', background: '#fffcfc' }}>
          <h3 style={{ color: 'var(--google-red)' }}>Advanced System Settings</h3>
          <div style={{ marginTop: '1.5rem' }}>
            <div className="form-group checkbox-group" style={{ background: 'white', padding: '1rem', borderRadius: '8px', border: '1px solid var(--google-grey-200)' }}>
              <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '1rem', cursor: 'pointer' }}>
                <input 
                  type="checkbox" 
                  checked={systemSettings.disable_admin_password_login === 'true'} 
                  onChange={(e) => handleUpdateSetting('disable_admin_password_login', e.target.checked)}
                  style={{ width: '20px', height: '20px' }}
                />
                <div>
                  <span style={{ fontWeight: '600', fontSize: '1rem' }}>Disable Admin Username/Password Login</span>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: 'var(--google-grey-600)', fontWeight: '400' }}>
                    When enabled, the primary 'admin' account cannot log in with a password. Access will only be possible via whitelisted Google accounts.
                  </p>
                </div>
              </label>
            </div>
          </div>
        </div>
      )}

      <div className="form-section">
        <h3>Add New User</h3>
        <form onSubmit={handleCreate} className="compact-form">
          <div className="form-row">
            <div className="form-group">
              <label>Username</label>
              <input value={newUser.username} onChange={e => setNewUser({...newUser, username: e.target.value})} required />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input type="password" value={newUser.password} onChange={e => setNewUser({...newUser, password: e.target.value})} required />
            </div>
            <div className="form-group">
              <label>Role</label>
              <select value={newUser.role} onChange={e => setNewUser({...newUser, role: e.target.value})}>
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>
          <div className="form-actions">
            <button type="submit" className="submit-btn">Create User</button>
          </div>
        </form>
      </div>

      <ConfirmationModal 
        isOpen={deleteConfirm.isOpen}
        title="Delete User"
        message="Are you sure you want to delete this user? This action cannot be undone."
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, id: null })}
      />
    </div>
  );
}

export default UserManagement;
